import React, { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, X, Image, Loader2, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('hygad_token');

const ImageUpload = ({ images = [], onChange, maxImages = 5 }) => {
  const [uploading, setUploading] = useState([]);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  const uploadFile = async (file) => {
    const tempId = Math.random().toString(36).slice(2);
    setUploading(prev => [...prev, tempId]);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${BACKEND_URL}/api/upload/image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Upload failed');
      const fileUrl = `${BACKEND_URL}/api/files/${data.path}`;
      onChange([...images, fileUrl]);
    } catch (err) {
      setError(err.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(prev => prev.filter(id => id !== tempId));
    }
  };

  const handleFiles = useCallback((files) => {
    const remaining = maxImages - images.length;
    if (remaining <= 0) { setError(`Maximum ${maxImages} photos allowed`); return; }
    const toUpload = Array.from(files).slice(0, remaining);
    toUpload.forEach(uploadFile);
  }, [images, maxImages]);

  const handleDrop = (e) => {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e) => e.preventDefault();

  const removeImage = (idx) => {
    const next = images.filter((_, i) => i !== idx);
    onChange(next);
  };

  const isUploading = uploading.length > 0;
  const isFull = images.length >= maxImages;

  return (
    <div data-testid="image-upload">
      {/* Preview grid */}
      {images.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-3">
          <AnimatePresence>
            {images.map((url, i) => (
              <motion.div key={url}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="relative group rounded-xl overflow-hidden bg-gray-100"
                style={{ aspectRatio: '1' }}
              >
                <img src={url} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => removeImage(i)}
                  data-testid={`remove-image-${i}`}
                  className="absolute top-1 right-1 bg-black/60 hover:bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-all"
                >
                  <X size={12} />
                </button>
                {i === 0 && (
                  <span className="absolute bottom-1 left-1 bg-gold/90 text-white text-[9px] px-1.5 py-0.5 rounded font-medium">
                    Cover
                  </span>
                )}
              </motion.div>
            ))}

            {/* Uploading placeholders */}
            {uploading.map(id => (
              <motion.div key={id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="rounded-xl bg-gray-100 flex items-center justify-center"
                style={{ aspectRatio: '1' }}
              >
                <Loader2 size={20} className="text-gold animate-spin" />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* Drop zone */}
      {!isFull && (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onClick={() => !isUploading && inputRef.current?.click()}
          className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors duration-200 ${
            isUploading ? 'border-gold/40 bg-gold/5 cursor-wait' : 'border-gray-200 hover:border-gold/50 hover:bg-gold/5'
          }`}
        >
          <input
            ref={inputRef}
            type="file"
            multiple
            accept="image/jpeg,image/jpg,image/png,image/webp"
            className="hidden"
            onChange={e => handleFiles(e.target.files)}
            data-testid="image-file-input"
          />
          {isUploading ? (
            <div className="flex flex-col items-center gap-2">
              <Loader2 size={24} className="text-gold animate-spin" />
              <p className="text-sm text-gray-500">Uploading {uploading.length} photo{uploading.length > 1 ? 's' : ''}...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <div className="w-10 h-10 bg-gold/10 rounded-xl flex items-center justify-center text-gold mx-auto">
                <Upload size={20} />
              </div>
              <div>
                <p className="text-sm font-medium text-navy">Click or drag photos here</p>
                <p className="text-xs text-gray-400 mt-0.5">
                  JPG, PNG, WEBP · Max 10MB each · {images.length}/{maxImages} uploaded
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {isFull && (
        <p className="text-xs text-gray-400 text-center py-2">
          Maximum {maxImages} photos uploaded. Remove one to add another.
        </p>
      )}

      {error && (
        <div className="flex items-center gap-2 mt-2 text-red-500 text-xs bg-red-50 px-3 py-2 rounded-lg">
          <AlertCircle size={13} />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
